import { useState, useRef, useEffect } from 'react'
import { createLead } from '@/services/leads'
import { SECTORS, KEYWORD_MAPPING, ChatOption } from '@/lib/chat-data'
import { useToast } from '@/hooks/use-toast'
import { logChatInteraction } from '@/services/chatbot-analytics'
import { getSiteSettings, CtaConfig } from '@/services/content'
import { getDialogueSteps, DialogueStep } from '@/services/chatbot-config'
import { processUserMessage } from '@/services/chat-ai'

export type Message = {
  id: string
  role: 'bot' | 'user'
  content: string
  type?: 'text' | 'carousel'
  isAi?: boolean
  cards?: { title: string; description: string }[]
}

export type ChatState = {
  mode: 'initial' | 'diagnostic' | 'exploration' | 'contact' | 'completed'
  step: string
  data: {
    maturity?: string
    dimension?: string
    manual_hours?: string
    pain_point?: string
    sector?: string
    name?: string
    company?: string
    email?: string
    phone?: string
    consent?: boolean
  }
  score: number
}

// Fallback initial message if DB fails
const DEFAULT_INITIAL_MESSAGE =
  'Se o teu negócio ainda depende de Excel e tarefas manuais, podes estar a perder tempo e margem todos os dias. Queres que eu faça 3 perguntas rápidas para estimar o impacto?'

export function useChatBot() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [state, setState] = useState<ChatState>({
    mode: 'initial',
    step: 'INIT',
    data: {},
    score: 0,
  })
  const [isTyping, setIsTyping] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // New State for Chatbot Config
  const [colors, setColors] = useState({ primary: '', secondary: '' })
  const [ctaConfig, setCtaConfig] = useState<CtaConfig[]>([])
  const [sessionId, setSessionId] = useState<string>('')

  // Dynamic Configuration
  const [dialogueSteps, setDialogueSteps] = useState<
    Record<string, DialogueStep>
  >({})
  const [configLoaded, setConfigLoaded] = useState(false)

  // Initialize Session ID and fetch settings
  useEffect(() => {
    // Generate simple UUID for session
    const sid = crypto.randomUUID()
    setSessionId(sid)

    const loadConfig = async () => {
      // 1. Load Settings (Colors, CTA)
      const { data: settings } = await getSiteSettings('pt', true)
      if (settings) {
        setColors({
          primary: settings.chatbot_primary_color || '',
          secondary: settings.chatbot_secondary_color || '',
        })
        setCtaConfig(settings.chatbot_cta_config || [])
      }

      // 2. Load Knowledge Base (Handled by Edge Function now for responses, but we load steps for flow)

      // 3. Load Dialogue Steps
      const { data: stepsData } = await getDialogueSteps()
      if (stepsData) {
        const stepsMap = stepsData.reduce(
          (acc, step) => {
            // Use step_key if available, fallback to step_identifier
            const key = step.step_key || step.step_identifier
            acc[key] = step
            return acc
          },
          {} as Record<string, DialogueStep>,
        )
        setDialogueSteps(stepsMap)

        // Initialize chat with INIT step message from DB if available
        const initStep = stepsMap['INIT']
        const initialMsg = initStep
          ? initStep.question_text || initStep.message_text
          : DEFAULT_INITIAL_MESSAGE

        setMessages([{ id: '1', role: 'bot', content: initialMsg }])

        // Log initial impression
        logChatInteraction({
          session_id: sid,
          user_message: null,
          bot_response: initialMsg,
          step_id: 'INIT',
          is_unanswered: false,
        })
      } else {
        // Fallback
        setMessages([
          { id: '1', role: 'bot', content: DEFAULT_INITIAL_MESSAGE },
        ])
      }

      setConfigLoaded(true)
    }

    loadConfig()
  }, [])

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }, 100)
  }

  const addMessage = (
    msg: Omit<Message, 'id'>,
    shouldScroll = true,
  ): Promise<void> => {
    return new Promise((resolve) => {
      const id = Date.now().toString()
      setMessages((prev) => [...prev, { ...msg, id }])
      if (shouldScroll) scrollToBottom()
      resolve()
    })
  }

  const simulateTyping = async (
    msg: Omit<Message, 'id'>,
    delay = 1000,
  ): Promise<void> => {
    setIsTyping(true)
    scrollToBottom()
    await new Promise((resolve) => setTimeout(resolve, delay))
    setIsTyping(false)
    await addMessage(msg)
  }

  const calculateImpact = () => {
    const { manual_hours, maturity } = state.data
    let hoursVal = 0
    let minPct = 0
    let maxPct = 0

    // Hours Logic
    if (manual_hours === 'lt5') hoursVal = 10
    else if (manual_hours === '5_15') hoursVal = 40
    else if (manual_hours === '15_30') hoursVal = 90
    else if (manual_hours === 'gt30') hoursVal = 140

    // Reduction Logic
    if (maturity === 'excel') {
      minPct = 40
      maxPct = 70
    } else if (maturity === 'software_mas_manual') {
      minPct = 25
      maxPct = 55
    } else if (maturity === 'sistemas_nao_comunicam') {
      minPct = 20
      maxPct = 45
    } else if (maturity === 'crescimento_sem_controlo') {
      minPct = 25
      maxPct = 60
    } else {
      minPct = 20
      maxPct = 50
    }

    return { hoursVal, minPct, maxPct }
  }

  // Get message from DB or fallback
  const getStepMessage = (stepId: string, defaultMsg: string) => {
    const step = dialogueSteps[stepId]
    return step ? step.question_text || step.message_text : defaultMsg
  }

  const handleOptionSelect = async (option: ChatOption) => {
    await addMessage({ role: 'user', content: option.label })

    const newScore = state.score + (option.score || 0)
    const newData = { ...state.data }

    // Determine next step from DB option configuration if available
    let nextStep = option.nextStep || ''

    // Apply logic based on CURRENT step to update data
    switch (state.step) {
      case 'INIT':
        if (option.value === 'diagnostico') {
          setState({
            mode: 'diagnostic',
            step: nextStep || 'MATURITY',
            data: newData,
            score: newScore,
          })
        } else if (option.value === 'falar') {
          setState({
            mode: 'contact',
            step: nextStep || 'CAPTURE_NAME',
            data: newData,
            score: newScore,
          })
        } else {
          setState({
            mode: 'exploration',
            step: nextStep || 'EXPLORE',
            data: newData,
            score: newScore,
          })
        }
        break

      case 'MATURITY':
        newData.maturity = option.value
        setState({
          ...state,
          step: nextStep || 'DIMENSION',
          data: newData,
          score: newScore,
        })
        break

      case 'DIMENSION':
        newData.dimension = option.value
        setState({
          ...state,
          step: nextStep || 'HOURS',
          data: newData,
          score: newScore,
        })
        break

      case 'HOURS':
        newData.manual_hours = option.value
        setState({
          ...state,
          step: nextStep || 'PAIN',
          data: newData,
          score: newScore,
        })
        break

      case 'PAIN':
        newData.pain_point = option.value
        setState({
          ...state,
          step: nextStep || 'SECTOR',
          data: newData,
          score: newScore,
        })
        break

      case 'SECTOR': {
        newData.sector = option.value
        nextStep = nextStep || 'IMPACT'
        const nextState = {
          ...state,
          step: nextStep,
          data: newData,
          score: newScore,
        }
        setState(nextState)

        const { hoursVal, minPct, maxPct } = calculateImpact()
        // Special dynamic message for IMPACT
        const botResponse = `Com base no que disseste, a tua equipa pode estar a gastar cerca de ${hoursVal} horas por mês em tarefas repetitivas. Normalmente, com automação e validação inteligente, conseguimos reduzir entre ${minPct}% e ${maxPct}% desse tempo.`

        await simulateTyping({ role: 'bot', content: botResponse })

        logChatInteraction({
          session_id: sessionId,
          user_message: option.label,
          bot_response: botResponse,
          step_id: nextStep,
          is_unanswered: false,
        })

        const sectorContent = SECTORS[option.value] || SECTORS['Outro']
        const carouselMsg = sectorContent.insight
        await simulateTyping({
          role: 'bot',
          content: carouselMsg,
          type: 'carousel',
          cards: sectorContent.cards,
        })

        const followUpStep = 'CAPTURE_NAME'
        setState({
          mode: 'contact',
          step: followUpStep,
          data: newData,
          score: newScore,
        })

        const followUpMsg = getStepMessage(
          followUpStep,
          'Gostarias de receber uma análise detalhada? Para isso, diz-me só o teu nome.',
        )

        await simulateTyping({ role: 'bot', content: followUpMsg }, 1500)

        logChatInteraction({
          session_id: sessionId,
          user_message: null,
          bot_response: followUpMsg,
          step_id: followUpStep,
          is_unanswered: false,
        })
        return
      }

      case 'CONSENT':
        if (option.value === 'yes') {
          newData.consent = true
          await submitLead(newData, newScore)
          return // handled in submitLead
        } else {
          const doneMsg =
            'Sem problema. Podes contactar-nos diretamente pelo email hello@nucleodigital.app se mudares de ideias.'
          await simulateTyping({ role: 'bot', content: doneMsg })
          setState({ ...state, mode: 'completed', step: 'DONE' })
          return
        }
    }

    // Default flow transition for simple steps
    if (state.step !== 'SECTOR' && nextStep) {
      const nextStepConfig = dialogueSteps[nextStep]
      const botResponse = nextStepConfig
        ? nextStepConfig.question_text || nextStepConfig.message_text
        : 'Prossiga...'

      await simulateTyping({ role: 'bot', content: botResponse })

      logChatInteraction({
        session_id: sessionId,
        user_message: option.label,
        bot_response: botResponse,
        step_id: nextStep,
        is_unanswered: false,
      })
    }
  }

  const handleTextInput = async (text: string) => {
    await addMessage({ role: 'user', content: text })
    const newData = { ...state.data }
    let botResponse = ''
    let isUnanswered = false
    let nextStep = state.step
    let isAiResponse = false

    // Exploration Mode: Use Edge Function for AI / Advanced Matching
    if (state.mode === 'exploration') {
      setIsTyping(true)
      const aiResult = await processUserMessage(text, sessionId)
      setIsTyping(false)

      botResponse = aiResult.response
      isAiResponse = aiResult.type === 'ai'

      await addMessage({
        role: 'bot',
        content: botResponse,
        isAi: isAiResponse,
      })

      // If specific sector was detected by regex (legacy fallback logic), we might want to guide them
      // But AI should handle it now. We stick to exploration mode unless user explicitly asks to start diagnostic.
      // For now, we assume AI keeps them in loop or answers query.

      logChatInteraction({
        session_id: sessionId,
        user_message: text,
        bot_response: botResponse,
        step_id: state.step,
        is_unanswered: false,
      })

      return
    }

    // Contact Flow
    switch (state.step) {
      case 'CAPTURE_NAME':
        newData.name = text
        nextStep = 'CAPTURE_COMPANY'
        setState({ ...state, step: nextStep, data: newData })
        botResponse = getStepMessage(
          nextStep,
          'Obrigado. E qual é o nome da tua empresa?',
        )
        break
      case 'CAPTURE_COMPANY':
        newData.company = text
        nextStep = 'CAPTURE_EMAIL'
        setState({ ...state, step: nextStep, data: newData })
        botResponse = getStepMessage(
          nextStep,
          'Qual o teu melhor email para contacto?',
        )
        break
      case 'CAPTURE_EMAIL':
        if (!text.includes('@') || text.length < 5) {
          botResponse =
            'Hmm, esse email não parece válido. Podes tentar de novo?'
          break
        }
        newData.email = text
        nextStep = 'CAPTURE_PHONE'
        setState({ ...state, step: nextStep, data: newData })
        botResponse = getStepMessage(
          nextStep,
          'Por fim, qual o teu contacto telefónico? (Opcional)',
        )
        break
      case 'CAPTURE_PHONE':
        newData.phone = text
        nextStep = 'CONSENT'
        setState({ ...state, step: nextStep, data: newData })
        botResponse = getStepMessage(
          nextStep,
          'Autorizas que a equipa do Núcleo Digital te contacte para apresentar esta análise?',
        )
        break
    }

    if (botResponse) {
      await simulateTyping({ role: 'bot', content: botResponse })
      logChatInteraction({
        session_id: sessionId,
        user_message: text,
        bot_response: botResponse,
        step_id: nextStep,
        is_unanswered: isUnanswered,
      })
    }
  }

  const submitLead = async (data: ChatState['data'], score: number) => {
    setState({ ...state, mode: 'completed', step: 'SUBMITTING' })
    await simulateTyping({
      role: 'bot',
      content: 'A guardar as tuas informações...',
    })

    const summary = `Diagnóstico: Setor ${data.sector || 'N/A'}, Maturidade: ${data.maturity || 'N/A'}, Dimensão: ${data.dimension || 'N/A'}, Horas Manuais: ${data.manual_hours || 'N/A'}, Problema: ${data.pain_point || 'N/A'}.`

    try {
      await createLead({
        full_name: data.name || 'Visitante',
        email: data.email || '',
        company: data.company,
        phone_number: data.phone,
        business_area: data.sector,
        message: 'Lead via Chatbot Diagnóstico',
        employee_count: data.dimension,
        uses_excel: data.maturity === 'excel',
        score: score,
        summary: summary,
      })

      await simulateTyping({
        role: 'bot',
        content:
          'Recebido! A nossa equipa vai analisar os dados e entrará em contacto brevemente. Obrigado!',
      })

      logChatInteraction({
        session_id: sessionId,
        user_message: 'Consent Given',
        bot_response: 'Lead Created',
        step_id: 'DONE',
        is_unanswered: false,
      })
    } catch (error) {
      const errMsg =
        'Houve um erro técnico ao guardar o contacto. Por favor envia um email para hello@nucleodigital.app.'
      await simulateTyping({ role: 'bot', content: errMsg })
      logChatInteraction({
        session_id: sessionId,
        user_message: 'Error',
        bot_response: errMsg,
        step_id: 'ERROR',
        is_unanswered: false,
      })
    }
  }

  const handleCtaClick = (label: string) => {
    logChatInteraction({
      session_id: sessionId,
      user_message: `Clicked CTA: ${label}`,
      bot_response: null,
      step_id: state.step,
      is_unanswered: false,
    })
  }

  // Get current options based on step from DB
  const getOptions = (): ChatOption[] => {
    const stepConfig = dialogueSteps[state.step]
    if (stepConfig && stepConfig.options && stepConfig.options.length > 0) {
      return stepConfig.options
    }
    return []
  }

  return {
    isOpen,
    setIsOpen,
    messages,
    state,
    isTyping,
    handleOptionSelect,
    handleTextInput,
    getOptions,
    scrollRef,
    scrollToBottom,
    colors,
    ctaConfig,
    handleCtaClick,
    configLoaded,
  }
}
